import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, switchMap, take, map, from } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';
import { AuthService } from '../auth/auth.service';

/**
 * Guard that resolves the tenant from the :slug route parameter
 * and ensures the current user has access and appropriate roles.
 */
export const tenantGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const tenant = inject(TenantService);
    const auth = inject(AuthService);
    const router = inject(Router);

    const slug = route.paramMap.get('slug');
    const platformId = inject(PLATFORM_ID);

    // On server, skip checks to prevent redirect/flash
    if (!isPlatformBrowser(platformId)) {
        return true;
    }

    if (!slug) {
        return toObservable(auth.isLoading).pipe(
            filter((loading) => !loading),
            take(1),
            switchMap(async () => {
                if (auth.isAuthenticated()) {
                    const url = await auth.getRedirectUrl();
                    return router.parseUrl(url);
                }
                return router.createUrlTree(['/login']);
            })
        );
    }

    return toObservable(auth.isLoading).pipe(
        filter((loading) => !loading),
        take(1),
        switchMap(() => {
            if (!auth.isAuthenticated()) {
                return from([router.createUrlTree(['/login'])]);
            }

            // Resolve the tenant context from the slug
            return from(tenant.resolveBySlug(slug)).pipe(
                map((resolved) => {
                    if (!resolved) {
                        // Church not found — redirect to onboarding or home
                        console.warn(`Church slug "${slug}" not found.`);
                        return router.createUrlTree(['/onboarding']);
                    }

                    // If accessing admin area, check roles
                    const isAdminPath = route.url.some(segment => segment.path === 'admin');
                    if (isAdminPath && !auth.hasAnyRole('admin', 'pastor', 'ministry_leader', 'super_admin')) {
                        console.warn(`Access denied to admin path for slug: ${slug}`);
                        return router.createUrlTree(['/unauthorized']);
                    }

                    return true;
                })
            );
        })
    );
};
